/**
 * @fileOverview The parts of the walkthrough's sandbox the app needs without loading the sandbox: the
 * mark on a refused write (read by `services/api.js` in every session) and the network guard beneath
 * axios. The sandbox itself, with the whole sample company, is loaded only inside the frame — see
 * `sandbox.ts` and docs/view-help.md ("The sandbox").
 */

/** Marks an error as the sandbox refusing a write, so `services/api.js` passes over it quietly. */
export const BLOCKED = Symbol.for("lewara.viewHelpBlocked")

/** Methods that only read. Anything else is a write and is refused. */
export const READ_METHODS = new Set(["get", "head", "options"])

/** True for the error the sandbox raises when it refuses a write. */
export function isViewHelpBlocked(error: unknown): boolean {
  return Boolean(error && typeof error === "object" && (error as Record<symbol, unknown>)[BLOCKED])
}

/**
 * The last line of defence, beneath axios: a write that somehow does not go through `apiClient` —
 * a file upload widget with its own `XMLHttpRequest`, a stray `fetch` — is refused here too.
 *
 * Patching globals is heavy-handed anywhere else. Here it is the point: this instance exists only to
 * be looked at, and there is nothing in it that should ever be able to change anything.
 */
export function installNetworkGuard(win: Window & typeof globalThis = window): void {
  const XHR = win.XMLHttpRequest
  const open = XHR.prototype.open
  const send = XHR.prototype.send
  const methods = new WeakMap<XMLHttpRequest, string>()

  XHR.prototype.open = function openGuarded(this: XMLHttpRequest, method: string, ...rest: unknown[]) {
    methods.set(this, String(method).toLowerCase())
    return (open as (..._args: unknown[]) => void).call(this, method, ...rest)
  } as typeof XHR.prototype.open

  XHR.prototype.send = function sendGuarded(this: XMLHttpRequest, body?: Parameters<XMLHttpRequest["send"]>[0]) {
    const method = methods.get(this) ?? "get"
    if (!READ_METHODS.has(method)) {
      // Behave like a request that failed on the wire, which every caller already has to handle.
      queueMicrotask(() => this.dispatchEvent(new Event("error")))
      return
    }
    return send.call(this, body)
  }

  const originalFetch = win.fetch.bind(win)
  win.fetch = function fetchGuarded(...args: Parameters<typeof fetch>) {
    const [input, init] = args
    const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toLowerCase()
    if (!READ_METHODS.has(method)) return Promise.reject(new TypeError("Refused by the walkthrough's sandbox"))
    return originalFetch(...args)
  } as typeof win.fetch
}
